<template>
  <div v-bem="{ disabled: $attrs.disabled }">
    <input
      v-for="(input, index) in length"
      :id="generateInputId(index)"
      :ref="generateInputId(index)"
      :key="index"
      v-model="inputValues[index]"
      v-bem:box="{ shiftNext: indexesWithSpace.includes(index) }"
      type="text"
      maxlength="1"
      contenteditable="true"
      placeholder="0"
      :disabled="$attrs.disabled"
      @keydown="handleKeydown"
      @keyup="handleInputFocus(index)"
      @paste.prevent="handlePastedValues"
      @input="returnFullString()"
    />
  </div>
</template>

<script>
const CHAR_PLACEHOLDER = '_';
const SPACE_PLACEHOLDER = ' ';

export default {
  name: 'MaskedInput',
  props: {
    mask: {
      type: String,
      required: true,
    },
    allowPaste: {
      type: Boolean,
      default: true,
      required: false,
    },
    onlyNumber: {
      type: Boolean,
      default: false,
      required: false,
    },
    value: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      inputValues: [],
    };
  },
  computed: {
    length() {
      return this.mask.split('').filter((char) => char === CHAR_PLACEHOLDER)
        .length;
    },
    indexesWithSpace() {
      let foundSpaces = 0;
      // Replace multiple whitespaces with a single whitespace. Also remove all
      // invalid characters (everything besides whitespace and underscore)
      const chars = this.mask
        .split('')
        .filter((char) => [CHAR_PLACEHOLDER, SPACE_PLACEHOLDER].includes(char))
        .join('')
        .replace(/\s+/g, SPACE_PLACEHOLDER)
        .split('');

      return chars
        .map((char, index) => {
          const nextIndex = index + 1 + foundSpaces;
          if (
            nextIndex < chars.length &&
            chars[nextIndex] === SPACE_PLACEHOLDER
          ) {
            foundSpaces += 1;
            return index;
          }
          return false;
        })
        .filter((value) => typeof value === 'number');
    },
  },
  mounted() {
    if (typeof this.value === 'string' && this.value.length) {
      this.inputValues = this.value.split('');
    }
  },
  methods: {
    handleKeydown(event) {
      if (!this.onlyNumber) {
        return;
      }
      const key = event.charCode || event.keyCode || 0;
      if (
        !(
          key === 8 ||
          key === 46 ||
          key === 86 ||
          key === 91 ||
          (key >= 48 && key <= 57) ||
          (key >= 96 && key <= 105)
        )
      ) {
        event.preventDefault();
      }
    },
    generateInputId(index) {
      return `fk_${index + 1}`;
    },
    handleInputFocus(index) {
      if (
        this.inputValues[index] &&
        this.inputValues[index] !== '' &&
        index < this.length - 1
      ) {
        const [nextInput] = this.$refs[`fk_${index + 2}`];
        nextInput.focus();
      } else if (
        index > 0 &&
        (!this.inputValues[index] || this.inputValues[index] === '')
      ) {
        const [previusInput] = this.$refs[`fk_${index}`];
        previusInput.focus();
      }
    },
    handlePastedValues(event) {
      if (this.allowPaste) {
        const pastedValue = event.clipboardData.getData('text/plain');
        const splitValues = pastedValue.split('');
        let canPaste = true;
        if (this.onlyNumber) {
          const regx = new RegExp(`^\\d{${this.length}}$`);
          canPaste = regx.test(pastedValue);
        }
        if (canPaste) {
          // eslint-disable-next-line no-plusplus
          for (let i = 0; i < this.length; i++) {
            this.updateInputValue(i, splitValues[i]);
          }
          const [lastInput] = this.$refs[`fk_${this.length}`];
          lastInput.focus();
          this.returnFullString();
        }
      }
    },
    updateInputValue(index, value) {
      this.$set(this.inputValues, index, value);
    },
    returnFullString() {
      this.$emit('input', this.inputValues.join(''));
    },
  },
};
</script>

<style lang="scss" scoped>
@use '~tokens/sass/utils';
@use "sass:map";

.masked-input {
  display: flex;
  border: 1px solid utils.$bs-input-border-color;
  border-radius: utils.$bs-input-border-radius;
  padding: utils.$bs-input-padding-y utils.f-rem-calc(3);
  width: fit-content;

  @at-root #{&}--disabled {
    background-color: utils.$bs-input-disabled-bg;
    border-color: utils.$bs-input-disabled-border-color;
  }

  // Remove the checkmark since we have no design and there is not enough space
  // to render it anyway.
  &.is-valid {
    background-image: none;
  }

  @at-root #{&}__box {
    display: block;
    width: utils.f-rem-calc(20);
    height: utils.f-rem-calc(20);
    margin: 0;
    padding: 0;
    border: none;
    color: utils.$bs-input-color;
    text-align: center;

    & + & {
      border-left: 1px solid map.get(utils.$v-colors, rw-grey-03);
    }

    &:first-child {
      margin-left: 0;
    }

    &:focus {
      outline: none;
    }

    @at-root #{&}--shift-next {
      margin-right: utils.f-rem-calc(18);
      border-right: 1px solid map.get(utils.$v-colors, rw-grey-03);
    }
  }
}
</style>
